import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../services/authentication/authContext';
import { ChevronRight, CreditCard, ExternalLink, User, Shield, Scale } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { primaryText, semiBoldText, xxLargeTextSize } from '../../styling/stylingHelper';
import { saveCurrentScrollPosition, restoreScrollPosition } from '../../utils/scrollUtils';
import MobilePageHeader from '../shared/mobile/MobilePageHeader';

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { backendUser } = useAuth();
  const { isActive, loading } = useSubscription();

  const stripePortalLink = process.env.REACT_APP_STRIPE_PORTAL_LINK || '';
  useEffect(() => {
    console.log('[SETTINGS PAGE] stripePortalLink', stripePortalLink);
  }, [stripePortalLink]);

  // Restore scroll position when returning from a legal detail page
  useEffect(() => {
    restoreScrollPosition();
  }, []);

  const handleManageSubscription = () => {
    if (stripePortalLink) {
      window.open(stripePortalLink, '_blank', 'noopener,noreferrer');
    }
  };

  const getSubscriptionStatusBadge = (status: string) => {
    const statusUpper = status.toUpperCase();

    switch (statusUpper) {
      case 'ACTIVE':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 dark:border-emerald-500/30">
            <div className="w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400"></div>
            Active
          </span>
        );
      case 'INACTIVE':
      case 'PENDING':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-amber-500/10 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/20 dark:border-amber-500/30">
            <div className="w-2 h-2 rounded-full bg-amber-500 dark:bg-amber-400"></div>
            {statusUpper === 'INACTIVE' ? 'Inactive' : 'Pending'}
          </span>
        );
      case 'CANCELED':
      case 'CANCELLED':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-400 border border-red-500/20 dark:border-red-500/30">
            <div className="w-2 h-2 rounded-full bg-red-500 dark:bg-red-400"></div>
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-zinc-500/10 dark:bg-zinc-500/20 text-zinc-600 dark:text-zinc-400 border border-zinc-500/20 dark:border-zinc-500/30">
            <div className="w-2 h-2 rounded-full bg-zinc-500 dark:bg-zinc-400"></div>
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div>
        <div className="bg-card dark:bg-card-dark shadow-lg rounded-2xl p-6">
          <p className="text-muted dark:text-muted-dark">{t('settings.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <MobilePageHeader title={t('settings.title')} />
      <div className="mb-6">
      <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText, 'hidden lg:block')}>
            {t('settings.title')}
          </h1>
      </div>

      <div className="space-y-4">
        {/* Account Information */}
        <div className="bg-card dark:bg-card-dark shadow-lg rounded-2xl p-6 space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
              <User className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
            </div>
            <h2 className="text-xl font-semibold text-text dark:text-text-dark">{t('settings.accountInformation')}</h2>
          </div>

          <div className="space-y-4 pl-13">
            <div className="flex justify-between items-start py-3 border-b border-zinc-200 dark:border-zinc-700">
              <div>
                <h3 className="text-sm font-semibold text-muted dark:text-muted-dark">{t('settings.email')}</h3>
                <p className="text-text dark:text-text-dark mt-1">{backendUser?.email}</p>
              </div>
            </div>

            <div className="flex justify-between items-start py-3 border-b border-zinc-200 dark:border-zinc-700">
              <div>
                <h3 className="text-sm font-semibold text-muted dark:text-muted-dark">{t('settings.role')}</h3>
                <div className="mt-2">
                  {backendUser?.role === 'admin' ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-500/20 dark:border-blue-500/30">
                      <Shield className="w-3 h-3" />
                      {t('settings.administrator')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-zinc-500/10 dark:bg-zinc-500/20 text-zinc-600 dark:text-zinc-400 border border-zinc-500/20 dark:border-zinc-500/30">
                      <User className="w-3 h-3" />
                      {t('settings.staff')}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-start py-3">
              <div>
                <h3 className="text-sm font-semibold text-muted dark:text-muted-dark">{t('settings.memberSince')}</h3>
                <p className="text-text dark:text-text-dark mt-1">
                  {backendUser?.created_at
                    ? new Date(backendUser.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Management - Only show to admin users */}
        {backendUser?.role === 'admin' && (
          <div className="bg-card dark:bg-card-dark shadow-lg rounded-2xl p-6 space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
              </div>
              <h2 className="text-xl font-semibold text-text dark:text-text-dark">{t('settings.subscription')}</h2>
            </div>

            <div className="space-y-4 pl-13">
              <div className="flex justify-between items-start py-3 border-b border-zinc-200 dark:border-zinc-700">
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-muted dark:text-muted-dark mb-2">{t('settings.status')}</h3>
                  <div>
                    {backendUser?.subscription_status
                      ? getSubscriptionStatusBadge(backendUser.subscription_status)
                      : <span className="text-muted dark:text-muted-dark text-sm">{t('settings.noSubscription')}</span>
                    }
                  </div>
                </div>
              </div>

              {isActive && stripePortalLink && (
                <div className="pt-2">
                  <button
                    onClick={handleManageSubscription}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition font-medium"
                  >
                    <CreditCard className="w-4 h-4" />
                    {t('settings.manageSubscription')}
                    <ExternalLink className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-muted dark:text-muted-dark mt-3">
                    {t('settings.manageSubscriptionDescription')}
                  </p>
                </div>
              )}

              {!isActive && (
                <div className="pt-2">
                  <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      {t('settings.subscriptionInactive')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Staff User Info */}
        {backendUser?.role === 'staff' && (
          <div className="bg-card dark:bg-card-dark shadow-lg rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
              </div>
              <h2 className="text-xl font-semibold text-text dark:text-text-dark">{t('settings.subscription')}</h2>
            </div>

            <div className="pl-13">
              <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {t('settings.staffSubscriptionInfo')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Legal — always available; opens each policy as an in-app detail page */}
        <div className="bg-card dark:bg-card-dark shadow-lg rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
              <Scale className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
            </div>
            <h2 className="text-xl font-semibold text-text dark:text-text-dark">{t('settings.legal')}</h2>
          </div>

          <div className="pl-13">
            <p className="text-xs text-muted dark:text-muted-dark mb-3">{t('settings.legalDescription')}</p>
            <div className="space-y-2">
              {[
                { to: '/settings/legal/terms-and-conditions', label: t('settings.termsAndConditions') },
                { to: '/settings/legal/privacy-policy', label: t('settings.privacyPolicy') },
                { to: '/settings/legal/cookies-policy', label: t('settings.cookiesPolicy') },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={saveCurrentScrollPosition}
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
                >
                  <span className="text-sm font-medium text-text dark:text-text-dark">{link.label}</span>
                  <ChevronRight className="w-4 h-4 text-muted dark:text-muted-dark flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
